import React from "react";
import { useTranslation } from "react-i18next";
import { useGuestCheckoutSettings, useUsesDesign } from "../context/GuestCheckoutSettingsContext";
import BookingReference from "./BookingReference";

type HeaderMode = "full" | "successOnly" | "successWithDetails" | "bookingOnly" | "bookingWithPreorders" | "enquiry";

interface BookingConfirmedHeaderProps {
  venueName: string;
  bookingRef: string;
  mode?: HeaderMode;
  /**
   * When set, always render the booking-journey title ("Thank you for your
   * booking!" in the app, "Thank you for your booking" in the widget) and
   * message regardless of `mode`. Used on /confirm for the /book
   * journey, whose outcome is always a booking (never a bare payment).
   */
  bookingJourney?: boolean;
}

/**
 * An i18next key beside the English it falls back to. Kept adjacent as
 * `"key", "fallback"` so the pattern translationFallbacks.test.ts matches
 * still finds them here.
 */
type Copy = readonly [key: string, fallback: string];

const BOOKING_ONLY_MESSAGE: Copy = [
  "booking_only_confirmed_message",
  "Thanks! Your booking at {{venue}} is confirmed. You will receive confirmation by email shortly.",
];

/**
 * The title and message each mode states, as a record keyed on the mode union
 * — so a mode cannot be added without its copy. The twelve `mode === ...`
 * branches this replaced could not enforce that, and stated the same two
 * elements six times over.
 */
const MODE_COPY: Record<HeaderMode, { readonly title: Copy; readonly message: Copy }> = {
  full: {
    title: ["booking_confirmed_title", "Booking Confirmed!"],
    message: [
      "booking_confirmed_message",
      "Thank you for your payment. Your table reservation at {{venue}} has been successfully confirmed.",
    ],
  },
  successOnly: {
    title: ["payment_confirmed_title", "Payment Confirmed!"],
    message: ["success_message", "Thank you for your payment."],
  },
  successWithDetails: {
    title: ["order_confirmed_title", "Order Confirmed!"],
    message: ["success_message_order", "Thank you for your order."],
  },
  bookingOnly: {
    title: ["booking_thank_you_booking", "Thank you for your booking!"],
    message: BOOKING_ONLY_MESSAGE,
  },
  bookingWithPreorders: {
    title: ["booking_thank_you_booking_with_preorders", "Thank you for your booking and pre-order"],
    message: [
      "booking_with_preorders_confirmed_message",
      "Thanks! Your booking and pre-order at {{venue}} are confirmed. You will receive confirmation by email shortly.",
    ],
  },
  enquiry: {
    title: ["booking_thank_you_enquiry", "Thank you for your enquiry"],
    message: [
      "booking_enquiry_received_message",
      "We have successfully received your booking enquiry and will get back to you as soon as possible. You will be contacted by our team shortly.",
    ],
  },
};

/** The widget drops the exclamation mark from the thank-you title. */
const WIDGET_THANK_YOU: Copy = ["widget_booking_thank_you_booking", "Thank you for your booking"];

/**
 * The widget's header is a left-aligned block at every width. The wider
 * surfaces take that alignment on a narrow measure and centre from `lg` up,
 * where a centred title reads as the page heading it is rather than as the
 * start of a column. A surface still on the legacy look is centred
 * throughout, as before.
 */
function headerAlignment(isWidget: boolean, usesDesign: boolean): string {
  if (isWidget) {
    return "gcw:items-start gcw:text-start";
  }
  if (usesDesign) {
    return "gcw:items-start gcw:text-start gcw:lg:items-center gcw:lg:text-center";
  }
  return "gcw:items-center gcw:text-center";
}

const BookingConfirmedHeader: React.FC<BookingConfirmedHeaderProps> = ({
  venueName,
  bookingRef,
  mode,
  bookingJourney,
}) => {
  const { t } = useTranslation();
  const { isWidget } = useGuestCheckoutSettings();
  const usesDesign = useUsesDesign();

  const containerClass = `gcw:flex gcw:flex-col ${headerAlignment(isWidget, usesDesign)} gcw:mb-bs-4`;

  // The booking journey's outcome is always a booking, so it states the
  // thank-you copy whatever mode it was handed. Without either, the header is
  // just the tick.
  let copy: { readonly title: Copy; readonly message: Copy } | undefined;
  if (bookingJourney) {
    copy = { title: MODE_COPY.bookingOnly.title, message: BOOKING_ONLY_MESSAGE };
  } else if (mode) {
    copy = MODE_COPY[mode];
  }

  // The widget's own spelling, which only the thank-you title has.
  const isThankYou = bookingJourney === true || mode === "bookingOnly";
  const title = isThankYou && isWidget ? WIDGET_THANK_YOU : copy?.title;

  return (
    <div className={containerClass}>
      {copy && title && <h1 className="payment-title gcw:font-bold gcw:mt-bs-4">{t(title[0], title[1])}</h1>}
      {copy && <p className="gcw:text-confirm">{t(copy.message[0], copy.message[1], { venue: venueName })}</p>}
      {/* The /book confirmation modes surface the reference in BookingDetailsCard instead. */}
      {!bookingJourney && mode === "full" && <BookingReference bookingRef={bookingRef} />}
    </div>
  );
};

export default BookingConfirmedHeader;
